/**
 * Integrytas Group — Email Distribution System (EDS)
 * Page: EDS.OPT.OUT  —  Opt-Out / Unsubscription Management
 *
 * Grid showing all opt-out records (global and list-scoped).
 * Supports:
 *   • Scope filter (all / global / list-scoped)
 *   • Manual opt-out entry via drawer
 *   • Reinstate (admin re-subscribe) action — sets reinstated_at
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { formatDate } from '../../lib/format_date';
import { apiDb } from '../../lib/api_client';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { compareStrings, useSort } from '../../lib/use_sort';
import type { EdsOptOutRow, EdsOptOutReasonRow, EdsRecipientListRow, EdsOptOutSortKey } from '../../types/eds';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';

const PAGE_CODE = 'EDS.OPT.OUT';

type ScopeFilter = 'all' | 'global' | 'list';

const GRID_COLS: GridColumnDef[] = [
  { key: 'email_address',  header: 'Email',       defaultWidth: 220 },
  { key: 'scope',          header: 'Scope',       defaultWidth: 120 },
  { key: 'reason_code',    header: 'Reason',      defaultWidth: 120 },
  { key: 'method_code',    header: 'Method',      defaultWidth: 140 },
  { key: 'opted_out_at',   header: 'Opted Out',   defaultWidth: 140 },
  { key: 'reinstated_at',  header: 'Reinstated',  defaultWidth: 120 },
];

// Opt-out method → badge variant (label comes localized from eds_opt_out_method_tl).
const METHOD_VARIANT: Record<string, StatusVariant> = {
  unsubscribe_link: 'info',
  manual:           'neutral',
  spam_complaint:   'danger',
  bounce_auto:      'warning',
};

export interface EdsOptOutPageProps { session: AuthSession; }

export function EdsOptOutPage({ session }: EdsOptOutPageProps) {
  return (
    <PersonalizationProvider pageCode="EDS.OPT.OUT" session={session}>
      <EdsOptOutPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EdsOptOutPageInner({ session }: EdsOptOutPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]             = useState<EdsOptOutRow[]>([]);
  const [filter, setFilter]         = useState('');
  const [scope, setScope]           = useState<ScopeFilter>('all');
  // §17.B5: per-column multi-value facets (reason, method).
  const [facets, setFacets]         = useState<{ reason: string[]; method: string[] }>({ reason: [], method: [] });
  // §17.B2: code → localized display_text from the reason / method lookups.
  const [reasonLabels, setReasonLabels] = useState<Record<string, string>>({});
  const [methodLabels, setMethodLabels] = useState<Record<string, string>>({});
  const [creating, setCreating]     = useState(false);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const sort = useSort<EdsOptOutSortKey>('eds.optout.sort', { key: 'opted_out_at', dir: 'desc' });
  const detail = useExpandedRows<string>();

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_opt_out')
        .select(`
          *,
          eds_recipient!left(email_address),
          eds_recipient_list!left(internal_code, eds_recipient_list_tl!left(list_name, language_id))
        `)
        .order('opted_out_at', { ascending: false })
        .limit(500);
      if (e) throw e;
      type ListTl = TlRowText & { list_name?: string };
      type Row = EdsOptOutRow & {
        eds_recipient?: { email_address?: string } | null;
        eds_recipient_list?: { internal_code?: string; eds_recipient_list_tl?: ListTl[] } | null;
      };
      const flat = ((data ?? []) as Row[]).map(r => {
        const ltls = r.eds_recipient_list?.eds_recipient_list_tl ?? [];
        const tl = ltls.find(t => t.language_id === activeLocale)   // §8: active locale first
                    ?? ltls.find(t => t.language_id === 'EN_US') ?? ltls[0];
        return {
          ...r,
          email_address: r.eds_recipient?.email_address ?? '',
          list_name: r.list_id ? (tl?.list_name ?? r.eds_recipient_list?.internal_code ?? r.list_id) : null,
        } as EdsOptOutRow;
      });
      setRows(flat);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
    // activeLocale: the _tl label picked below must follow the active language.
  }, [activeLocale]);

  useEffect(() => { void reload(); }, [reload]);

  // §17.B2: load reason/method lookups + their _tl, resolved to the active locale.
  useEffect(() => {
    const locale = activeLocale;
    const buildMap = (rows: { code: string; language_id: string; display_text: string }[]) => {
      const byCode: Record<string, { language_id: string; display_text: string }[]> = {};
      for (const r of rows) (byCode[r.code] ??= []).push(r);
      const out: Record<string, string> = {};
      for (const [code, tls] of Object.entries(byCode)) {
        const exact = tls.find(t => t.language_id === locale);
        const fb = tls.find(t => t.language_id === 'EN_US') ?? tls[0];
        out[code] = exact?.display_text ?? fb?.display_text ?? code;
      }
      return out;
    };
    void apiDb.from('eds_opt_out_reason_tl').select('reason_code, language_id, display_text')
      .then(({ data }) => setReasonLabels(buildMap((data ?? []).map((r: { reason_code: string; language_id: string; display_text: string }) => ({ code: r.reason_code, language_id: r.language_id, display_text: r.display_text })))));
    void apiDb.from('eds_opt_out_method_tl').select('method_code, language_id, display_text')
      .then(({ data }) => setMethodLabels(buildMap((data ?? []).map((r: { method_code: string; language_id: string; display_text: string }) => ({ code: r.method_code, language_id: r.language_id, display_text: r.display_text })))));
  }, [activeLocale]);

  const reinstate = async (r: EdsOptOutRow) => {
    if (!confirm(t('eds.opt_out.reinstate_confirm', 'Reinstate {{email}}? This marks the opt-out as reversed but does NOT automatically re-add them to any list.', { email: r.email_address }))) return;
    setBusy(true); setError(null);
    const actor = getForensicActorId(session);
    try {
      const { error: e } = await routedUpdate(session, 'eds_opt_out', 'opt_out_id', r.opt_out_id, {
        reinstated_at: new Date().toISOString(),
        reinstated_by: actor,
      });
      if (e) throw e;
      // If global opt-out, clear the flag on recipient
      if (!r.list_id) {
        const { error: recipientErr } = await routedUpdate(session, 'eds_recipient', 'recipient_id', r.recipient_id, {
          is_globally_opted_out: 'N',
        });
        if (recipientErr) {
          setError(recipientErr.message ?? t('eds.opt_out.error_recipient_update', 'Failed to update recipient status'));
          setBusy(false);
          return;
        }
      }
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  // §17.B5: distinct facet options derived from loaded rows + lookups.
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      reason: uniq(rows.map(r => r.reason_code)).map(v => ({ value: v, label: reasonLabels[v] ?? v })),
      method: uniq(rows.map(r => r.method_code)).map(v => ({ value: v, label: methodLabels[v] ?? v })),
    };
  }, [rows, reasonLabels, methodLabels]);

  const facetsActive = facets.reason.length + facets.method.length > 0;
  const anyFilterActive = facetsActive || scope !== 'all' || filter.trim().length > 0;
  const clearAll = () => {
    setFilter('');
    setScope('all');
    setFacets({ reason: [], method: [] });
  };

  const filtered = useMemo(() => {
    const base = rows.filter(r => {
      const matchText = !filter.trim() || (r.email_address ?? '').toLowerCase().includes(filter.toLowerCase());
      const matchScope = scope === 'all' || (scope === 'global' ? !r.list_id : !!r.list_id);
      // OR within a facet, AND across facets.
      const matchFacets =
        (facets.reason.length === 0 || facets.reason.includes(r.reason_code)) &&
        (facets.method.length === 0 || facets.method.includes(r.method_code));
      return matchText && matchScope && matchFacets;
    });
    return sort.sorted(base, (a, b, key) => compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? '')));
  }, [rows, filter, scope, facets, sort]);

  // §17.B13 related-children: for list-scoped opt-outs, the parent list (resolved name; raw code as title).
  const buildRelatedGroups = (id: string): RelatedChildGroup[] => {
    const r = rows.find(x => x.opt_out_id === id);
    return [
      { key: 'list', title: t('eds.opt_out.related.list', 'List'),
        items: r && r.list_id && r.list_name ? [{ id: r.list_id, label: r.list_name }] : [] },
    ];
  };
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('eds.opt_out.title', 'Opt-Outs')}
          description={t('eds.opt_out.list.description', 'Manage global and list-scoped unsubscriptions, and reinstate recipients when appropriate.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('eds.opt_out.add_opt_out')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('eds.opt_out.load_failed', "Couldn't load opt-out records.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                     placeholder={t('eds.opt_out.filter_placeholder', 'Filter by email…')}
                     className="h-8 w-64 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <div className="flex gap-1">
              {(['all', 'global', 'list'] as ScopeFilter[]).map(s => (
                <button key={s} onClick={() => setScope(s)}
                        className={`h-8 rounded-csm-control border px-2 text-csm-xs font-medium capitalize transition-colors
                          ${scope === s ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}`}>
                  {s === 'all' ? t('eds.opt_out.filter.all') : s === 'global' ? t('eds.opt_out.filter.global') : t('eds.opt_out.filter.list_scoped')}
                </button>
              ))}
            </div>
            <FacetFilter label={t('eds.opt_out.columns.reason', 'Reason')} options={facetOptions.reason} selected={facets.reason} onChange={v => setFacets(f => ({ ...f, reason: v }))} />
            <FacetFilter label={t('eds.opt_out.columns.method', 'Method')} options={facetOptions.method} selected={facets.method} onChange={v => setFacets(f => ({ ...f, method: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              <span className="text-csm-xs text-csm-muted">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('eds.opt_out.personalization_hint', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => {
                    if (col.key === 'scope') {
                      return (
                        <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                            style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>
                          {col.header}
                        </th>
                      );
                    }
                    return (
                      <SortableHeader<EdsOptOutSortKey> key={col.key}
                        sortKey={col.key as EdsOptOutSortKey} label={col.header}
                        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                        style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                    );
                  })}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.opt_out_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.opt_out_id)}
                        hasChildren={hasRelated(r.opt_out_id)}
                        onToggle={() => detail.toggle(r.opt_out_id)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'email_address' && <span className="font-mono text-csm-sm">{r.email_address}</span>}
                        {col.key === 'scope' && (
                          r.list_id
                            ? <span className="rounded bg-csm-info-soft px-1.5 py-0.5 text-csm-xs text-csm-info border border-csm-info-border">{r.list_name ?? t('eds.opt_out.scope_list', 'List')}</span>
                            : <span className="rounded bg-csm-danger-soft px-1.5 py-0.5 text-csm-xs text-csm-danger border border-csm-danger-border font-medium">{t('eds.opt_out.scope_global', 'GLOBAL')}</span>
                        )}
                        {col.key === 'reason_code'   && <span className="text-csm-ink-soft">{reasonLabels[r.reason_code] ?? r.reason_code}</span>}
                        {col.key === 'method_code'   && (
                          <StatusBadge
                            variant={METHOD_VARIANT[r.method_code] ?? 'neutral'}
                            label={methodLabels[r.method_code] ?? r.method_code}
                          />
                        )}
                        {col.key === 'opted_out_at'  && <span className="text-csm-sm text-csm-ink-soft">{new Date(r.opted_out_at).toLocaleString()}</span>}
                        {col.key === 'reinstated_at' && (
                          r.reinstated_at
                            ? <span className="text-csm-sm text-csm-success">{formatDate(r.reinstated_at, activeLocale)}</span>
                            : <span className="text-csm-sm italic text-csm-muted">—</span>
                        )}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {!r.reinstated_at && (
                        <button onClick={() => void reinstate(r)}
                                className="rounded border border-csm-success-border bg-csm-success-soft px-2 py-0.5 text-csm-xs text-csm-success hover:opacity-80">
                          {t('eds.opt_out.reinstate')}
                        </button>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.opt_out_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.opt_out_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('eds.opt_out.no_results')}
                      action={!anyFilterActive
                        ? canAdd && (<button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">+ {t('eds.opt_out.add_opt_out')}</button>)
                        : undefined}
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {creating && (
        <OptOutModal
          session={session}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// MANUAL OPT-OUT MODAL
// ===========================================================================

function OptOutModal({ session, onClose, onSaved }: { session: AuthSession; onClose: () => void; onSaved: () => void; }) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer' as const;

  const buildInitial = () => ({
    email: '',
    listId: '',
    reasonCode: 'user_request',
    notes: '',
  });

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(buildInitial());
  const [showDiscard, setShowDiscard] = useState(false);
  const [reasons, setReasons]       = useState<EdsOptOutReasonRow[]>([]);
  const [lists, setLists]           = useState<EdsRecipientListRow[]>([]);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);

  useEffect(() => {
    void apiDb.from('eds_opt_out_reason').select('reason_code, sort_order').order('sort_order')
      .then(({ data }) => setReasons((data ?? []) as EdsOptOutReasonRow[]));
    void apiDb.from('eds_recipient_list')
      .select(`list_id, internal_code, eds_recipient_list_tl!left(list_name, language_id)`)
      .eq('is_active', 'Y')
      .then(({ data }) => {
        type ListTl = TlRowText & { list_name?: string };
        type Row = EdsRecipientListRow & { eds_recipient_list_tl?: ListTl[] };
        const flat = ((data ?? []) as Row[]).map(r => {
          const ltls = r.eds_recipient_list_tl ?? [];
          const tl = ltls.find(t => t.language_id === activeLocale)   // §8: active locale first
                     ?? ltls.find(t => t.language_id === 'EN_US') ?? ltls[0];
          return { ...r, list_name: tl?.list_name ?? r.internal_code } as EdsRecipientListRow;
        });
        setLists(flat);
      });
  }, [activeLocale]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!form.email.trim()) throw new Error(t('eds.opt_out.error_email_required', 'Email address is required.'));
      // Resolve recipient_id
      const { data: recip, error: e1 } = await apiDb
        .from('eds_recipient').select('recipient_id').eq('email_address', form.email.trim().toLowerCase()).single();
      if (e1 || !recip) throw new Error(t('eds.opt_out.error_recipient_not_found', 'Recipient not found: {{email}}', { email: form.email.trim() }));
      const recipientId = (recip as { recipient_id: string }).recipient_id;
      const { error: e2 } = await routedInsert(session, 'eds_opt_out', {
        recipient_id: recipientId,
        list_id: form.listId || null,
        reason_code: form.reasonCode,
        method_code: 'manual',
        notes: form.notes.trim() || null,
        opted_out_at: new Date().toISOString(),
      });
      if (e2) throw e2;
      // Mark global opted out if no list_id
      if (!form.listId) {
        await routedUpdate(session, 'eds_recipient', 'recipient_id', recipientId, { is_globally_opted_out: 'Y' });
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const reasonOptions = reasons.map(r => ({ internal_code: r.reason_code, display_text: r.reason_code }));
  const listOptions   = [
    { internal_code: '', display_text: t('eds.opt_out.drawer.global_all_lists', '(Global — all lists)') },
    ...lists.map(l => ({ internal_code: l.list_id, display_text: l.list_name ?? l.internal_code })),
  ];

  return (
    <>
      <Modal
        title={t('eds.opt_out.drawer.title')}
        onClose={handleClose}
        width="lg"
        headerAction={<PersonalizationToggle />}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving') : t('eds.opt_out.add_opt_out')}
            </button>
          </>
        }
      >
        {isPersonalizing && (
          <div className="-mt-4 mb-3 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
            {t('eds.opt_out.drawer.personalization_hint', 'Toggle fields — hide or mark required. Click icon to save.')}
          </div>
        )}
        <div className="space-y-3">
          <div className="rounded border border-csm-warning-border bg-csm-warning-soft px-3 py-2 text-csm-xs text-csm-warning">
            {t('eds.opt_out.drawer.warning', 'This action is permanent and auditable. Leave "Scope" as global to block all future email to this address.')}
          </div>
          <PersonalizedField componentKey={CK} fieldKey="email_address" label={t('eds.opt_out.drawer.fields.email_address')}>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.opt_out.drawer.fields.email_address')} <span className="text-csm-danger">*</span></label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                   className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
          </PersonalizedField>
          <PersonalizedField componentKey={CK} fieldKey="list_id" label={t('eds.opt_out.drawer.fields.list_scope')}>
            <FilteredModalSingleSelect
              label={`${t('eds.opt_out.drawer.fields.list_scope')} (${t('eds.opt_out.drawer.fields.list_scope_hint')})`}
              options={listOptions}
              selectedCode={form.listId}
              onChange={c => setForm(f => ({ ...f, listId: c ?? '' }))}
              allowClear={false}
            />
          </PersonalizedField>
          <PersonalizedField componentKey={CK} fieldKey="reason_code" label={t('eds.opt_out.drawer.fields.reason_code')}>
            <FilteredModalSingleSelect
              label={t('eds.opt_out.drawer.fields.reason_code')}
              options={reasonOptions}
              selectedCode={form.reasonCode}
              onChange={c => { if (c) setForm(f => ({ ...f, reasonCode: c })); }}
              allowClear={false}
            />
          </PersonalizedField>
          <PersonalizedField componentKey={CK} fieldKey="notes" label={t('eds.opt_out.drawer.fields.notes')}>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.opt_out.drawer.fields.notes')}</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder={t('eds.opt_out.drawer.notes_placeholder', 'Optional admin notes…')}
                      className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-primary focus:outline-none" />
          </PersonalizedField>
          {error && <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        </div>
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('eds.discard.title', 'Discard changes?')}
          message={t('eds.discard.message_lost', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('eds.discard.confirm', 'Discard')}
          cancelLabel={t('eds.discard.cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
